/**
 * analytics.config.js
 * 
 * Configuration centralisée de Firebase Analytics.
 * 
 * Fonctionnalités :
 * - Événements personnalisés pour le tracking utilisateur
 * - Paramètres utilisateur (user properties)
 * - Tracking navigation (screen_view)
 * - Événements métier (login, logout, actions importantes)
 * - Métriques de performance
 * - Events de conversion
 * 
 * Utilisation :
 * ```javascript
 * import { logScreenView, logEvent, setUserProperties } from './analytics.config';
 * 
 * // Dans un composant
 * logScreenView('home');
 * logEvent('button_click', { button_name: 'submit' });
 * setUserProperties({ role: 'Admin' });
 * ```
 */

import { getAnalyticsInstance } from '@/services/firebase';
import { logEvent as firebaseLogEvent, setUserId, setUserProperties as firebaseSetUserProperties } from 'firebase/analytics';
import { canUseAnalytics } from './features/legal/legalStorage';

// Instance Analytics (sera initialisée de manière asynchrone)
let analyticsInstance = null;
let isInitialized = false;

/**
 * Initialise Firebase Analytics.
 * Appelée automatiquement au premier événement, mais peut être appelée manuellement.
 * @returns {Promise<boolean>} true si initialisé avec succès
 */
export const initAnalytics = async () => {
  if (isInitialized) {
    return true;
  }

  if (!canUseAnalytics()) {
    return false;
  }

  try {
    analyticsInstance = await getAnalyticsInstance();
    if (analyticsInstance) {
      console.log('📊 Firebase Analytics initialisé');
      isInitialized = true;
      return true;
    } else {
      console.warn('⚠️ Firebase Analytics non disponible');
      return false;
    }
  } catch (error) {
    console.error('❌ Erreur initialisation Analytics:', error);
    return false;
  }
};

/**
 * Log un événement personnalisé dans Analytics.
 * @param {string} eventName - Nom de l'événement (snake_case recommandé)
 * @param {Object} [params] - Paramètres de l'événement
 * @returns {Promise<void>}
 * 
 * @example
 * logEvent('user_login', { method: 'google' });
 * logEvent('notification_sent', { recipient: 'user123', type: 'email' });
 */
export const logEvent = async (eventName, params = {}) => {
  try {
    if (!canUseAnalytics()) {
      return;
    }

    if (!isInitialized) {
      await initAnalytics();
    }

    if (analyticsInstance) {
      // Enrichir avec timestamp
      const enrichedParams = {
        ...params,
        timestamp: new Date().toISOString(),
        environment: import.meta.env.MODE || 'development',
      };

      firebaseLogEvent(analyticsInstance, eventName, enrichedParams);
      
      // Log en dev uniquement
      if (import.meta.env.MODE === 'development') {
        console.log(`📊 Analytics Event: ${eventName}`, enrichedParams);
      }
    }
  } catch (error) {
    console.error(`❌ Erreur logEvent ${eventName}:`, error);
  }
};

/**
 * Log une vue d'écran (page view).
 * @param {string} screenName - Nom de l'écran/page
 * @param {Object} [params] - Paramètres additionnels
 * @returns {Promise<void>}
 * 
 * @example
 * logScreenView('admin_dashboard', { section: 'users' });
 */
export const logScreenView = async (screenName, params = {}) => {
  await logEvent('screen_view', {
    screen_name: screenName,
    ...params,
  });
};

/**
 * Log une connexion utilisateur.
 * @param {string} method - Méthode de connexion ('google', 'email', etc.)
 * @param {Object} [params] - Paramètres additionnels
 * @returns {Promise<void>}
 */
export const logLogin = async (method, params = {}) => {
  await logEvent('login', {
    method,
    ...params,
  });
};

export const logSignup = async (method = 'email', params = {}) => {
  await logEvent('signup', {
    method,
    ...params,
  });
};

/**
 * Log une déconnexion utilisateur.
 * @param {Object} [params] - Paramètres additionnels
 * @returns {Promise<void>}
 */
export const logLogout = async (params = {}) => {
  await logEvent('logout', params);
};

export const logPhotoUploaded = async (params = {}) => {
  await logEvent('photo_uploaded', params);
};

export const logNotificationPermission = async (status, params = {}) => {
  await logEvent('notification_permission', {
    status,
    ...params,
  });
};

export const logPageView = async (pagePath, params = {}) => {
  await logEvent('page_view', {
    page_path: pagePath,
    page_title: typeof document !== 'undefined' ? document.title : undefined,
    ...params,
  });
};

/**
 * Log l'envoi d'une notification.
 * @param {string} type - Type de notification ('push', 'email', 'sms')
 * @param {Object} [params] - Paramètres additionnels
 * @returns {Promise<void>}
 */
export const logNotificationSent = async (type, params = {}) => {
  await logEvent('notification_sent', {
    notification_type: type,
    ...params,
  });
};

/**
 * Log une erreur applicative.
 * @param {string} errorMessage - Message d'erreur
 * @param {Object} [params] - Contexte de l'erreur
 * @returns {Promise<void>}
 */
export const logError = async (errorMessage, params = {}) => {
  await logEvent('app_error', {
    error_message: errorMessage,
    ...params,
  });
};

/**
 * Log une action utilisateur importante.
 * @param {string} action - Nom de l'action
 * @param {Object} [params] - Paramètres de l'action
 * @returns {Promise<void>}
 * 
 * @example
 * logUserAction('photo_uploaded', { count: 5 });
 * logUserAction('issue_created', { priority: 'high' });
 */
export const logUserAction = async (action, params = {}) => {
  await logEvent('user_action', {
    action_name: action,
    ...params,
  });
};

/**
 * Log une conversion (objectif atteint).
 * @param {string} conversionName - Nom de la conversion
 * @param {number} [value] - Valeur de la conversion
 * @param {Object} [params] - Paramètres additionnels
 * @returns {Promise<void>}
 * 
 * @example
 * logConversion('signup_completed', 1, { plan: 'free' });
 */
export const logConversion = async (conversionName, value = 1, params = {}) => {
  await logEvent(conversionName, {
    value,
    ...params,
  });
};

/**
 * Log une recherche.
 * @param {string} searchTerm - Terme recherché
 * @param {Object} [params] - Paramètres additionnels
 * @returns {Promise<void>}
 */
export const logSearch = async (searchTerm, params = {}) => {
  await logEvent('search', {
    search_term: searchTerm,
    ...params,
  });
};

/**
 * Log un partage.
 * @param {string} contentType - Type de contenu partagé
 * @param {string} method - Méthode de partage
 * @param {Object} [params] - Paramètres additionnels
 * @returns {Promise<void>}
 */
export const logShare = async (contentType, method, params = {}) => {
  await logEvent('share', {
    content_type: contentType,
    method,
    ...params,
  });
};

/**
 * Définit l'ID utilisateur pour Analytics.
 * @param {string|null} userId - ID de l'utilisateur (null pour clear)
 * @returns {Promise<void>}
 * 
 * @example
 * setAnalyticsUserId('user_abc123');
 * setAnalyticsUserId(null); // Sur logout
 */
export const setAnalyticsUserId = async (userId) => {
  try {
    if (!canUseAnalytics()) {
      return;
    }

    if (!isInitialized) {
      await initAnalytics();
    }

    if (analyticsInstance) {
      setUserId(analyticsInstance, userId);
      console.log(`📊 Analytics User ID: ${userId || 'cleared'}`);
    }
  } catch (error) {
    console.error('❌ Erreur setAnalyticsUserId:', error);
  }
};

/**
 * Définit des propriétés utilisateur (user properties).
 * Utile pour segmenter les utilisateurs dans Analytics.
 * 
 * @param {Object} properties - Propriétés utilisateur
 * @returns {Promise<void>}
 * 
 * @example
 * setUserProperties({
 *   role: 'Admin',
 *   subscription_plan: 'premium',
 *   account_age_days: 365
 * });
 */
export const setUserProperties = async (properties) => {
  try {
    if (!canUseAnalytics()) {
      return;
    }

    if (!isInitialized) {
      await initAnalytics();
    }

    if (analyticsInstance) {
      firebaseSetUserProperties(analyticsInstance, properties);
      
      if (import.meta.env.MODE === 'development') {
        console.log('📊 Analytics User Properties:', properties);
      }
    }
  } catch (error) {
    console.error('❌ Erreur setUserProperties:', error);
  }
};

/**
 * Configuration des événements Analytics recommandés.
 * 
 * Événements prédéfinis Firebase (automatiques si supportés) :
 * - first_open : Première ouverture de l'app
 * - session_start : Début de session
 * - user_engagement : Engagement utilisateur
 * - page_view : Vue de page (web)
 * 
 * Événements custom recommandés :
 * - screen_view : Navigation entre écrans
 * - login : Connexion utilisateur
 * - logout : Déconnexion
 * - notification_sent : Notification envoyée
 * - notification_received : Notification reçue
 * - app_error : Erreur applicative
 * - user_action : Action importante
 * - conversion events : Objectifs atteints
 */

export const ANALYTICS_EVENTS = {
  // Navigation
  SCREEN_VIEW: 'screen_view',
  
  // Authentification
  LOGIN: 'login',
  LOGOUT: 'logout',
  SIGN_UP: 'signup',
  SIGNUP: 'signup',

  // Tracking demandé issue #169
  PHOTO_UPLOADED: 'photo_uploaded',
  NOTIFICATION_PERMISSION: 'notification_permission',
  PAGE_VIEW: 'page_view',
  
  // Notifications
  NOTIFICATION_SENT: 'notification_sent',
  NOTIFICATION_RECEIVED: 'notification_received',
  NOTIFICATION_OPENED: 'notification_opened',
  
  // Actions utilisateur
  USER_ACTION: 'user_action',
  BUTTON_CLICK: 'button_click',
  FORM_SUBMIT: 'form_submit',
  
  // Erreurs
  APP_ERROR: 'app_error',
  API_ERROR: 'api_error',
  
  // Performance
  PAGE_LOAD_TIME: 'page_load_time',
  API_RESPONSE_TIME: 'api_response_time',
  
  // Conversion
  CONVERSION: 'conversion',
  GOAL_COMPLETED: 'goal_completed',
  
  // Recherche et découverte
  SEARCH: 'search',
  SHARE: 'share',
  
  // Contenu
  FILE_UPLOAD: 'file_upload',
  FILE_DOWNLOAD: 'file_download',
};

/**
 * Configuration des paramètres Analytics recommandés.
 */
export const ANALYTICS_PARAMS = {
  // User properties
  ROLE: 'role',
  ENVIRONMENT: 'environment',
  VERSION: 'app_version',
  
  // Event params
  SCREEN_NAME: 'screen_name',
  METHOD: 'method',
  ACTION_NAME: 'action_name',
  ERROR_MESSAGE: 'error_message',
  NOTIFICATION_TYPE: 'notification_type',
  
  // Metrics
  VALUE: 'value',
  DURATION: 'duration',
  COUNT: 'count',
};

export default {
  initAnalytics,
  logEvent,
  logScreenView,
  logLogin,
  logSignup,
  logLogout,
  logPhotoUploaded,
  logNotificationPermission,
  logPageView,
  logNotificationSent,
  logError,
  logUserAction,
  logConversion,
  logSearch,
  logShare,
  setAnalyticsUserId,
  setUserProperties,
  ANALYTICS_EVENTS,
  ANALYTICS_PARAMS,
};
